import React, { useEffect, useRef, ReactNode } from 'react';
import { Modal } from 'react-responsive-modal';
import FocusTrap from 'focus-trap-react';
import { useRenderTracker } from '@app/shared/debug';

interface AccessibleModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  styles?: any;
  returnFocusRef?: React.RefObject<HTMLElement>;
  center?: boolean;
}

export const AccessibleModal: React.FC<AccessibleModalProps> = ({
  open,
  onClose,
  children,
  ariaLabelledBy,
  ariaDescribedBy,
  styles,
  returnFocusRef,
  center = true,
}) => {
  useRenderTracker('AccessibleModal');
  const lastFocusedElement = useRef<HTMLElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      // Store the last focused element when modal opens
      lastFocusedElement.current = document.activeElement as HTMLElement;

      // Prevent body scroll on mobile (disabled for iOS scroll testing)
      // document.body.style.overflow = 'hidden';
      // document.body.style.position = 'fixed';
      // document.body.style.width = '100%';
      // document.body.style.height = '100%';

      // Focus the modal content after a small delay to ensure it's rendered
      setTimeout(() => {
        if (modalContentRef.current) {
          const firstFocusable = modalContentRef.current.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (firstFocusable) {
            firstFocusable.focus();
          } else {
            modalContentRef.current.focus();
          }
        }
      }, 100);
    } else {
      // Re-enable body scroll (disabled for iOS scroll testing)
      // document.body.style.overflow = '';
      // document.body.style.position = '';
      // document.body.style.width = '';
      // document.body.style.height = '';

      // Return focus to the last focused element when modal closes
      if (returnFocusRef?.current) {
        returnFocusRef.current.focus();
      } else if (lastFocusedElement.current) {
        lastFocusedElement.current.focus();
      }
    }
  }, [open, returnFocusRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      center={center}
      blockScroll={false} // iOS fix: let us handle scroll blocking manually
      styles={styles}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {/* FocusTrap disabled temporarily for iOS scroll testing */}
      {/* <FocusTrap
        active={open}
        focusTrapOptions={{
          escapeDeactivates: true,
          clickOutsideDeactivates: true,
          returnFocusOnDeactivate: false,
          initialFocus: false,
          allowOutsideClick: true,
        }}
      > */}
      <div
        ref={modalContentRef}
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        {children}
      </div>
      {/* </FocusTrap> */}
    </Modal>
  );
};

